import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface MessageTemplate {
  id: string;
  title: string;
  channel: string;
  category: string | null;
  legalProductId: string | null;
  legalProductName: string | null;
  content: string;
  variables: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMessageTemplateInput {
  title: string;
  channel?: string;
  category?: string | null;
  legalProductId?: string | null;
  content: string;
  variables?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export type UpdateMessageTemplateInput = CreateMessageTemplateInput;

export type LoadMessageTemplatesFilters = {
  includeArchived?: boolean;
  search?: string;
  channel?: string;
  category?: string;
  legalProductId?: string;
};

type UseMessageTemplatesOptions = {
  autoLoad?: boolean;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toBoolean = (value: unknown, defaultValue = true) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value).trim().toLowerCase();

  if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
    return false;
  }

  if (['1', 'true', 'active', 'ativo', 'sim', 's', 'on'].includes(normalized)) {
    return true;
  }

  return Boolean(value);
};

const sortMessageTemplates = (a: MessageTemplate, b: MessageTemplate) =>
  b.updatedAt.localeCompare(a.updatedAt) || a.title.localeCompare(b.title, 'pt-BR');

const sortAndSetMessageTemplates = (
  setMessageTemplates: Dispatch<SetStateAction<MessageTemplate[]>>,
  next: MessageTemplate[]
) => {
  setMessageTemplates([...next].sort(sortMessageTemplates));
};

const normalizeMessageTemplate = (value: any): MessageTemplate => ({
  id: String(value?.id ?? ''),
  title: String(value?.title ?? '').trim() || 'Sem título',
  channel: toNullableText(value?.channel) ?? 'whatsapp',
  category: toNullableText(value?.category),
  legalProductId: toNullableText(value?.legalProductId ?? value?.legal_product_id),
  legalProductName: toNullableText(value?.legalProductName ?? value?.legal_product_name),
  content: String(value?.content ?? ''),
  variables: toNullableText(value?.variables),
  isActive: toBoolean(value?.isActive ?? value?.is_active, true),
  notes: toNullableText(value?.notes),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

export function useMessageTemplates(options: UseMessageTemplatesOptions = {}) {
  const { autoLoad = true } = options;
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const lastFiltersRef = useRef<LoadMessageTemplatesFilters>({});

  const loadMessageTemplates = useCallback(async (filters: LoadMessageTemplatesFilters = {}) => {
    const requestId = ++requestIdRef.current;
    lastFiltersRef.current = filters;
    setLoading(true);
    setError(null);

    try {
      const searchParams = new URLSearchParams();

      if (filters.includeArchived) {
        searchParams.set('includeArchived', '1');
      }

      if (filters.search?.trim()) {
        searchParams.set('search', filters.search.trim());
      }

      if (filters.channel?.trim()) {
        searchParams.set('channel', filters.channel.trim());
      }

      if (filters.category?.trim()) {
        searchParams.set('category', filters.category.trim());
      }

      if (filters.legalProductId?.trim()) {
        searchParams.set('legalProductId', filters.legalProductId.trim());
      }

      const endpoint = searchParams.toString()
        ? `/api/message-templates?${searchParams.toString()}`
        : '/api/message-templates';

      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.messageTemplates)) {
        throw new Error(data.error || `GET /api/message-templates falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        sortAndSetMessageTemplates(setMessageTemplates, data.messageTemplates.map(normalizeMessageTemplate));
      }
    } catch (loadError) {
      console.error('Erro ao carregar mensagens prontas reais.', loadError);

      if (requestId === requestIdRef.current) {
        setError('Não foi possível carregar as mensagens prontas reais.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const refetch = useCallback(() => loadMessageTemplates(lastFiltersRef.current), [loadMessageTemplates]);

  const createMessageTemplate = useCallback(async (input: CreateMessageTemplateInput) => {
    const response = await fetchWithRetry('/api/message-templates', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.messageTemplate) {
      throw new Error(data.error || `POST /api/message-templates falhou com status ${response.status}`);
    }

    const created = normalizeMessageTemplate(data.messageTemplate);
    setMessageTemplates((prev) => [created, ...prev.filter((template) => template.id !== created.id)].sort(sortMessageTemplates));
    return created;
  }, []);

  const updateMessageTemplate = useCallback(async (id: string, input: UpdateMessageTemplateInput) => {
    const response = await fetch(`/api/message-templates/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.messageTemplate) {
      throw new Error(data.error || `PUT /api/message-templates/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeMessageTemplate(data.messageTemplate);
    setMessageTemplates((prev) => [updated, ...prev.filter((template) => template.id !== updated.id)].sort(sortMessageTemplates));
    return updated;
  }, []);

  const archiveMessageTemplate = useCallback(async (id: string) => {
    const response = await fetch(`/api/message-templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/message-templates/${id} falhou com status ${response.status}`);
    }

    const archivedAt = new Date().toISOString();
    setMessageTemplates((prev) =>
      prev.map((template) =>
        template.id === id
          ? {
              ...template,
              isActive: false,
              updatedAt: archivedAt,
            }
          : template
      )
    );
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadMessageTemplates();
  }, [autoLoad, loadMessageTemplates]);

  return {
    messageTemplates,
    loading,
    error,
    refetch,
    loadMessageTemplates,
    createMessageTemplate,
    updateMessageTemplate,
    archiveMessageTemplate,
  };
}
